import React, { useState, useEffect } from 'react';
import { Users, Search, Filter, CheckCircle, XCircle, Clock, Calendar, Loader2, ChevronDown, Phone, Mail, Send, CheckSquare, Square } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedApps, setSelectedApps] = useState([]);
  const [bulkStatusUpdate, setBulkStatusUpdate] = useState({
    status: '',
    remarks: '',
    interview_date: '',
    interview_time: '',
    interview_venue: ''
  });
  const [notificationData, setNotificationData] = useState({
    interview_date: '',
    interview_time: '',
    interview_venue: '',
    additional_info: ''
  });
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    remarks: '',
    interview_date: '',
    interview_time: '',
    interview_venue: '',
    offer_salary: ''
  });
  const token = localStorage.getItem('token');

  const statuses = [
    { value: 'applied', label: 'Applied', color: 'bg-blue-100 text-blue-800' },
    { value: 'shortlisted', label: 'Shortlisted', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-purple-100 text-purple-800' },
    { value: 'selected', label: 'Selected', color: 'bg-green-100 text-green-800' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    { value: 'offer_accepted', label: 'Offer Accepted', color: 'bg-emerald-100 text-emerald-800' },
    { value: 'offer_rejected', label: 'Offer Rejected', color: 'bg-gray-100 text-gray-800' }
  ];

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, [statusFilter, jobFilter]);

  const fetchApplications = async () => {
    try {
      let url = `${API_URL}/api/placement/applications`;
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (jobFilter) params.append('job_id', jobFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/placement/jobs?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const openStatusModal = (app) => {
    setSelectedApp(app);
    setStatusUpdate({
      status: '',
      remarks: '',
      interview_date: '',
      interview_time: '',
      interview_venue: '',
      offer_salary: ''
    });
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdate.status) {
      toast.error('Please select a status');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/api/placement/applications/${selectedApp.id}/status`,
        statusUpdate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Application status updated');
      setShowStatusModal(false);
      fetchApplications();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Toggle single application selection
  const toggleSelectApp = (appId) => {
    setSelectedApps(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  // Toggle all applications selection
  const toggleSelectAll = () => {
    if (selectedApps.length === filteredApplications.length) {
      setSelectedApps([]);
    } else {
      setSelectedApps(filteredApplications.map(app => app.id));
    }
  };

  // Bulk Status Update Handler
  const handleBulkStatusUpdate = async () => {
    if (!bulkStatusUpdate.status) {
      toast.error('Please select a status');
      return;
    }
    if (selectedApps.length === 0) {
      toast.error('Please select at least one application');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/placement/applications/bulk-update`,
        {
          application_ids: selectedApps,
          ...bulkStatusUpdate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Updated ${response.data.updated_count} applications`);
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      setShowBulkModal(false);
      setSelectedApps([]);
      setBulkStatusUpdate({ status: '', remarks: '', interview_date: '', interview_time: '', interview_venue: '' });
      fetchApplications();
    } catch (error) {
      console.error('Error bulk updating:', error);
      toast.error(error.response?.data?.detail || 'Failed to update applications');
    }
  };

  // Send Interview Notification Handler
  const handleSendNotification = async () => {
    if (selectedApps.length === 0) {
      toast.error('Please select at least one application');
      return;
    }
    if (!notificationData.interview_date || !notificationData.interview_time) {
      toast.error('Please provide interview date and time');
      return;
    }

    // Get company and job details from first selected application
    const firstApp = applications.find(app => app.id === selectedApps[0]);

    try {
      const response = await axios.post(
        `${API_URL}/api/placement/notifications/interview`,
        {
          application_ids: selectedApps,
          company_name: firstApp?.company_name,
          job_title: firstApp?.job_title,
          ...notificationData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Notifications sent to ${response.data.sent_count} students`);
      setShowNotificationModal(false);
      setSelectedApps([]);
      setNotificationData({ interview_date: '', interview_time: '', interview_venue: '', additional_info: '' });
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error(error.response?.data?.detail || 'Failed to send notifications');
    }
  };

  const getStatusStyle = (status) => {
    const found = statuses.find(s => s.value === status);
    return found ? found.color : 'bg-gray-100 text-gray-800';
  };

  const filteredApplications = applications.filter(app =>
    app.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.admission_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.job_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="applications-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Applications</h1>
          <p className="text-sm text-gray-500 mt-1">Manage student job applications</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by student name or admission number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            {statuses.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <select
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Jobs</option>
            {jobs.map(job => (
              <option key={job.id} value={job.id}>{job.title} - {job.company_name}</option>
            ))}
          </select>
        </div>

        {/* Bulk Action Buttons */}
        {selectedApps.length > 0 && (
          <div className="mt-4 pt-4 border-t flex flex-wrap gap-3 items-center">
            <span className="text-sm text-gray-600">{selectedApps.length} selected</span>
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
            >
              <CheckSquare className="h-4 w-4" /> Bulk Update Status
            </button>
            <button
              onClick={() => setShowNotificationModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm"
            >
              <Send className="h-4 w-4" /> Send Interview Notification
            </button>
            <button
              onClick={() => setSelectedApps([])}
              className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
            >
              Clear Selection
            </button>
          </div>
        )}
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button 
                    onClick={toggleSelectAll}
                    className="text-gray-500 hover:text-blue-600"
                  >
                    {selectedApps.length === filteredApplications.length && filteredApplications.length > 0 ? (
                      <CheckSquare className="h-5 w-5 text-blue-600" />
                    ) : (
                      <Square className="h-5 w-5" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredApplications.length > 0 ? (
                filteredApplications.map(app => (
                  <tr key={app.id} className={`hover:bg-gray-50 ${selectedApps.includes(app.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-4 py-4">
                      <button 
                        onClick={() => toggleSelectApp(app.id)}
                        className="text-gray-500 hover:text-blue-600"
                      >
                        {selectedApps.includes(app.id) ? (
                          <CheckSquare className="h-5 w-5 text-blue-600" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{app.student_name}</p>
                        <p className="text-sm text-gray-500">{app.admission_number}</p>
                        <div className="flex gap-2 mt-1">
                          {app.phone && (
                            <a href={`tel:${app.phone}`} className="text-gray-400 hover:text-blue-600">
                              <Phone className="h-3 w-3" />
                            </a>
                          )}
                          {app.email && (
                            <a href={`mailto:${app.email}`} className="text-gray-400 hover:text-blue-600">
                              <Mail className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{app.job_title}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{app.company_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{app.course_name}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(app.status)}`}>
                        {app.status?.replace(/_/g, ' ')}
                      </span>
                      {app.interview_date && app.status === 'interview_scheduled' && (
                        <p className="text-xs text-purple-600 mt-1">
                          <Calendar className="h-3 w-3 inline mr-1" />
                          {app.interview_date} {app.interview_time}
                        </p>
                      )}
                      {app.offer_salary && app.status === 'selected' && (
                        <p className="text-xs text-green-600 mt-1">
                          ₹{(app.offer_salary / 100000).toFixed(1)}L/year
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(app.applied_at)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openStatusModal(app)}
                        className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                      >
                        Update Status
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    No applications found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Update Application Status</h2>
              <p className="text-sm text-gray-500 mt-1">{selectedApp.student_name} - {selectedApp.job_title}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Status *</label>
                <select
                  value={statusUpdate.status}
                  onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Status</option>
                  {statuses.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {statusUpdate.status === 'interview_scheduled' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Interview Date</label>
                      <input
                        type="date"
                        value={statusUpdate.interview_date}
                        onChange={(e) => setStatusUpdate({ ...statusUpdate, interview_date: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Interview Time</label>
                      <input
                        type="time"
                        value={statusUpdate.interview_time}
                        onChange={(e) => setStatusUpdate({ ...statusUpdate, interview_time: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Venue</label>
                    <input
                      type="text"
                      value={statusUpdate.interview_venue}
                      onChange={(e) => setStatusUpdate({ ...statusUpdate, interview_venue: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Conference Room A"
                    />
                  </div>
                </>
              )}

              {statusUpdate.status === 'selected' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Offered Package (Annual)</label>
                  <input
                    type="number"
                    value={statusUpdate.offer_salary}
                    onChange={(e) => setStatusUpdate({ ...statusUpdate, offer_salary: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 500000"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  rows="2"
                  value={statusUpdate.remarks}
                  onChange={(e) => setStatusUpdate({ ...statusUpdate, remarks: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any notes..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Update Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Bulk Update Status</h2>
              <p className="text-sm text-gray-500 mt-1">Update {selectedApps.length} applications</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Status *</label>
                <select
                  value={bulkStatusUpdate.status}
                  onChange={(e) => setBulkStatusUpdate({ ...bulkStatusUpdate, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Status</option>
                  {statuses.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {bulkStatusUpdate.status === 'interview_scheduled' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Interview Date</label>
                      <input
                        type="date"
                        value={bulkStatusUpdate.interview_date}
                        onChange={(e) => setBulkStatusUpdate({ ...bulkStatusUpdate, interview_date: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Interview Time</label>
                      <input
                        type="time"
                        value={bulkStatusUpdate.interview_time}
                        onChange={(e) => setBulkStatusUpdate({ ...bulkStatusUpdate, interview_time: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Venue</label>
                    <input
                      type="text"
                      value={bulkStatusUpdate.interview_venue}
                      onChange={(e) => setBulkStatusUpdate({ ...bulkStatusUpdate, interview_venue: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Conference Room A"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  rows="2"
                  value={bulkStatusUpdate.remarks}
                  onChange={(e) => setBulkStatusUpdate({ ...bulkStatusUpdate, remarks: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any notes..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkStatusUpdate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update {selectedApps.length} Applications
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interview Notification Modal */}
      {showNotificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Send Interview Notification</h2>
              <p className="text-sm text-gray-500 mt-1">Send email to {selectedApps.length} students</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Interview Date *</label>
                  <input
                    type="date"
                    value={notificationData.interview_date}
                    onChange={(e) => setNotificationData({ ...notificationData, interview_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Interview Time *</label>
                  <input
                    type="time"
                    value={notificationData.interview_time}
                    onChange={(e) => setNotificationData({ ...notificationData, interview_time: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Venue</label>
                <input
                  type="text"
                  value={notificationData.interview_venue}
                  onChange={(e) => setNotificationData({ ...notificationData, interview_venue: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Main Campus, Conference Room A"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
                <textarea
                  rows="3"
                  value={notificationData.additional_info}
                  onChange={(e) => setNotificationData({ ...notificationData, additional_info: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Any additional instructions for students..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowNotificationModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendNotification}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Send className="h-4 w-4" /> Send Notification
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;
